import {useCallback, useEffect, useState} from 'react';

import * as Sentry from '@sentry/react';
import {number} from 'prop-types';
import Split from 'react-split';

import useMainSidebar from '../../../hooks/providers/useMainSidebar';
import ReportModulesList from '../ReportModulesList/ReportModulesList';
import ReportsList from '../ReportsList/ReportsList';

const getCursorMarkup = cursor => `
    <div id="sidebar-drag-handle" style="
      background: white;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: ${cursor};
      margin: 0 auto;
      box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
      margin-top: -13px;
      position: relative;
    ">
      <svg xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 0 24 24" width="16" fill="currentColor">
        <path d="M12 3l4 4h-3v10h3l-4 4-4-4h3V7H8l4-4z"/>
      </svg>
    </div>
  `;

const SplitReportAndModules = ({totalHeightNeededForReportsDisplaying}) => {
  const {isDragging, setIsDragging, closeAllSidebarRelatedPoppers} = useMainSidebar();
  const [panesSizes, setPanesSizes] = useState([50, 50]);

  const cursor = isDragging ? 'grabbing' : 'grab';

  const getCursor = useCallback(() => {
    const gutter = document.createElement('div');
    gutter.innerHTML = getCursorMarkup(cursor);
    return gutter;
  }, [isDragging]);

  // This hooks aims to transform cursor to "grabbing" while dragging, and "grab" when not dragging
  useEffect(() => {
    const handleElement = document.querySelector('#main-sidebar-drag-handle');
    if (handleElement) {
      handleElement.style.cursor = cursor;
    }
  }, [cursor]);

  // This hook aims to persist panes sizes to local storage after user has modified panes height.
  useEffect(() => {
    const isDefaultPanesSizes = panesSizes[0] === 50 && panesSizes[1] === 50;
    if (panesSizes && !isDefaultPanesSizes) {
      const stringified = JSON.stringify(panesSizes);
      localStorage.setItem('sidebarPanesSizes', stringified);
    }
  }, [panesSizes]);

  // This hook aims to set panes sizes from local storage is user has set custom panes sizes before

  useEffect(() => {
    try {
      const previousSavedPanesSizesString = localStorage.getItem('sidebarPanesSizes');
      const previousSavedPanesSizes = JSON.parse(previousSavedPanesSizesString);

      if (Array.isArray(previousSavedPanesSizes)) {
        setPanesSizes(previousSavedPanesSizes);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      Sentry.captureException('Failed to parse sidebarPanesSizes from localStorage: ', error.toString());
    }
  }, []);

  useEffect(() => {
    closeAllSidebarRelatedPoppers();

    const handle = document.querySelector('#sidebar-drag-handle');
    if (!handle) return;

    handle.style.cursor = isDragging ? 'grabbing' : 'grab';
  }, [isDragging]);

  return (
    <Split
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'nowrap',
        width: '100%',
        flex: 1
      }}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={sizes => {
        setPanesSizes(sizes);
        setIsDragging(false);
      }}
      gutter={getCursor}
      gutterSize={12}
      minSize={[60, 180]}
      sizes={panesSizes}
      snapOffset={0}
      dragInterval={1}
      direction="vertical"
      cursor="grab"
    >
      <ReportsList maxHeight={totalHeightNeededForReportsDisplaying} />
      <ReportModulesList />
    </Split>
  );
};

SplitReportAndModules.propTypes = {
  totalHeightNeededForReportsDisplaying: number.isRequired
};

export default SplitReportAndModules;
